// Marketing → Overview: is Threads connected, what is waiting, what posts in
// the next seven days, and when the daily brain last ran.
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ErrorNote, Note } from '../../components/ui';
import { ago } from '../../lib/format';
import { addDays, dayLabel, hstDateTime, hstToday, mk, type Stats } from '../../lib/marketing';
import { MkPage } from './shared';

const STATUS_WORDS: Record<string, string> = {
  ok: 'Connected',
  unconfigured: 'Not set up yet',
  blocked: 'Blocked by Meta',
  error: 'Check failed',
};

export function MarketingOverview() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [pending, setPending] = useState<number | undefined>();
  const [err, setErr] = useState<unknown>(null);

  useEffect(() => {
    let alive = true;
    mk.stats().then((s) => { if (!alive) return; setStats(s); setPending(s.inboxPending ?? undefined); }).catch((e) => alive && setErr(e));
    return () => { alive = false; };
  }, []);

  const today = hstToday();
  const days = Array.from({ length: 7 }, (_, i) => addDays(today, i));
  const byDay = new Map((stats?.next7 ?? []).map((d) => [d.day.slice(0, 10), d]));
  const status = stats?.health.status ?? 'unconfigured';
  const metaError = stats?.health.error ?? stats?.health.message ?? null;
  const run = stats?.lastBrainRun ?? null;

  return (
    <MkPage title="Overview" blurb="The Threads queue for the Academy app: what is waiting, what posts next, and whether the account is connected." pendingReplies={pending}>
      <ErrorNote error={err} notConfigured="Marketing is not set up on the server yet." />
      {!stats && !err && <div className="bk-grid">{[0, 1, 2, 3].map((i) => <div key={i} className="bk-card bk-span-3 bk-stat"><div className="bk-stat-label">Loading…</div><div className="bk-stat-num">&nbsp;</div></div>)}</div>}
      {stats && (
        <div className="bk-grid">
          <section className="bk-card bk-span-4">
            <div className="bk-card-head"><h2 className="bk-card-title">Threads account</h2>{stats.health.username ? <span className="bk-card-sub">@{String(stats.health.username)}</span> : null}</div>
            <div className="bk-card-body">
              <div className="mk-status">
                <span className={`mk-dot mk-dot--${status}`} aria-hidden="true" />
                <span className="mk-status-word">{STATUS_WORDS[status] ?? status}</span>
              </div>
              {status === 'unconfigured' && (
                <p className="bk-blurb" style={{ marginTop: 8 }}>Nothing posts until Leah creates the @rootegra Threads account and connects it. Approved posts wait safely in the queue.</p>
              )}
              {(status === 'blocked' || status === 'error') && metaError && <Note tone="warn">Meta said: {String(metaError)}</Note>}
              {status === 'ok' && stats.health.followers != null && <p className="bk-blurb" style={{ marginTop: 8 }}>{Number(stats.health.followers).toLocaleString('en-US')} followers</p>}
              {stats.health.checked_at ? <p className="bk-stat-sub">Checked {ago(String(stats.health.checked_at))}</p> : null}
            </div>
            <div className="bk-card-foot">
              {status === 'unconfigured'
                ? <Link className="bk-link" to="/help#set-up-threads">Set up Threads →</Link>
                : <Link className="bk-link" to="/marketing/settings">Account and health check →</Link>}
            </div>
          </section>

          <section className="bk-card bk-span-8">
            <div className="bk-card-head"><h2 className="bk-card-title">Queue</h2><span className="bk-card-sub">All Threads posts</span></div>
            <div className="bk-card-body">
              <div className="bk-grid mk-counts" style={{ gap: 10 }}>
                {([
                  ['Draft', stats.counts.draft, 'draft', 'Waiting for a yes'],
                  ['Approved', stats.counts.approved, 'approved', 'Post on schedule'],
                  ['Posted', stats.counts.posted, 'posted', 'Live on Threads'],
                  ['Failed', stats.counts.failed, 'failed', 'Last try did not post'],
                ] as const).map(([label, n, state, sub]) => (
                  <Link key={label} to={`/marketing/queue?state=${state}`} className="bk-card bk-span-3 bk-stat" style={{ boxShadow: 'none' }}>
                    <div className="bk-stat-label">{label}</div>
                    <div className={`bk-stat-num${state === 'failed' && n > 0 ? ' neg' : ''}`}>{n}</div>
                    <div className="bk-stat-sub">{sub}</div>
                  </Link>
                ))}
              </div>
            </div>
            <div className="bk-card-foot"><Link className="bk-link" to="/marketing/queue">Open the queue →</Link></div>
          </section>

          <section className="bk-card bk-span-12">
            <div className="bk-card-head"><h2 className="bk-card-title">Next 7 days</h2><span className="bk-card-sub">Hawaii time</span></div>
            <div className="bk-card-body">
              <div className="mk-strip">
                {days.map((d) => {
                  const c = byDay.get(d);
                  return (
                    <Link key={d} to={`/marketing/queue?day=${d}`} className={`mk-strip-day${d === today ? ' today' : ''}`}>
                      <div className="mk-strip-dow">{d === today ? 'Today' : dayLabel(d).split(',')[0]}</div>
                      <div className="mk-strip-date">{dayLabel(d).split(', ')[1]}</div>
                      <div className="mk-strip-num">{c?.count ?? 0}</div>
                      <div className="mk-strip-sub">{c ? (c.posted ? `${c.posted} posted` : `${c.approved} approved`) : '0 approved'}</div>
                    </Link>
                  );
                })}
              </div>
            </div>
          </section>

          <section className="bk-card bk-span-6">
            <div className="bk-card-head"><h2 className="bk-card-title">Last post</h2></div>
            <div className="bk-card-body">
              {stats.lastPost?.posted_at ? (
                <>
                  <p className="mk-post-body">{stats.lastPost.body}</p>
                  <div className="mk-post-meta">
                    <span>{hstDateTime(stats.lastPost.posted_at)}</span>
                    {stats.lastPost.permalink && <a href={stats.lastPost.permalink} target="_blank" rel="noopener noreferrer">View on Threads</a>}
                  </div>
                </>
              ) : <p className="lp-empty">Nothing has posted yet.</p>}
              {stats.nextPost?.scheduled_at && (
                <p className="bk-stat-sub" style={{ marginTop: 8 }}>Next approved post: {hstDateTime(stats.nextPost.scheduled_at)}</p>
              )}
            </div>
          </section>

          <section className="bk-card bk-span-6">
            <div className="bk-card-head"><h2 className="bk-card-title">Daily brain</h2><span className="bk-card-sub">Mac mini</span></div>
            <div className="bk-card-body">
              {run ? (
                <>
                  <div className="mk-post-meta" style={{ marginBottom: 6 }}>
                    <span className={`mk-chip mk-chip--${run.status === 'ok' ? 'approved' : run.status === 'failed' ? 'failed' : 'draft'}`}>{run.status === 'ok' ? 'Ran' : run.status === 'failed' ? 'Failed' : 'Skipped'}</span>
                    <span>{hstDateTime(run.finished_at ?? run.started_at)}</span>
                    {run.host && <span>on {run.host}</span>}
                  </div>
                  {run.summary && <p className="mk-post-body">{run.summary}</p>}
                  {run.error && <p className="mk-post-err">{run.error}</p>}
                </>
              ) : <p className="lp-empty">The brain has not run yet. It starts once the Mac mini is signed in.</p>}
            </div>
          </section>
        </div>
      )}
    </MkPage>
  );
}
